// @flow
import type { UploadFileDetails } from "uploader";
import getLogger from "common/logger";

const HAS_FILE_READ_SUPPORT = "FileReader" in self && "FileList" in self && "Blob" in self;

const logger = getLogger();
const K = 1024;
const SIZE_UNITS = ["b", "k", "m", "g", "t"];

const logToBase = (num, base) => Math.floor(Math.log(num) / Math.log(base));

const OFFICE_TYPES_MAP = {
	"application/vnd.openxmlformats-officedocument.presentationml.presentation": "powerpoint",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "word",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "excel",
};

const getSizeInfoFromBytes = (bytes: ?number, decimals: number = 2) => {
	let text = "na";
	let size = bytes;

	if (bytes && bytes > 0) {
		try {
			const mag = logToBase(parseInt(bytes), K);

			text = SIZE_UNITS[mag];

			if (mag > 0) {
				size = parseFloat((bytes / Math.pow(K, mag)).toFixed(decimals));
			}
		} catch (ex) {
			logger.error(
				`[fileUtils]: failed to parse bytes (${bytes}) into file size`,
				ex,
			);
		}
	}

	return { text, size };
};

const getFileType = (file: UploadFileDetails) => {
	let type = "";

	if (file.type) {
		type = OFFICE_TYPES_MAP[file.type] || file.type;
		type = type.replace(/^.*\//, "").replace(/\+.*$/, "").toUpperCase();
	} else if (file.name) {
		type = file.name.replace(/^.*\./, "").replace(/\?.*$/, "").toUpperCase();
	}

	return type.replace("JPEG", "JPG"); // Use "jpg" as the standard file extension to display for consistency with ML
};

const VIDEO_TYPE_RGX = /^video|audio\//;
const IMAGE_TYPE_RGX = /^image\//;
const CLD_IMAGE_TYPE_RGX = /\/((x-)?photoshop|psd)$/;

const isVideo = (file: File) => VIDEO_TYPE_RGX.test(file.type);

const isImage = (file: File) => IMAGE_TYPE_RGX.test(file.type);

/**
 * whether the file type matches a mimetype that Cloudinary considers to be an image (ex: psd)
 */
const isCloudinaryImage = (file: File) => CLD_IMAGE_TYPE_RGX.test(file.type);

const isFile = (obj: any) => (HAS_FILE_READ_SUPPORT && (obj instanceof File || obj.toString() === "[object File]"));

export {
	isFile,
	isCloudinaryImage,
	getSizeInfoFromBytes,
	getFileType,
	isImage,
	isVideo,
};
